import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Interpolation, keyframes, Theme } from '@emotion/react';
import { borderRadius } from '../tokens';
import { colors } from '../colors';
import CheckIcon from '../../../images/check-icon.svg';

export interface DropdownProps
    extends Omit<
    HtmlPropsNoRef<DropdownMenu.DropdownMenuTriggerProps>,
    'onChange' | 'onBlur' | 'title'
    > {
    children: any;
    title: React.ReactNode | string
    align?: 'start' | 'center' | 'end',
    dropdownProps?: {
        root?: DropdownMenu.DropdownMenuProps,
        trigger?: DropdownMenu.DropdownMenuTriggerProps  & React.RefAttributes<HTMLButtonElement>,
        portal?: DropdownMenu.DropdownMenuPortalProps,
        content?: DropdownMenu.DropdownMenuContentProps & React.RefAttributes<HTMLDivElement> }
}

const slideUpAndFade = keyframes({
    '0%': { opacity: 0, transform: 'translateY(2px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideRightAndFade = keyframes({
    '0%': { opacity: 0, transform: 'translateX(-2px)' },
    '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideDownAndFade = keyframes({
    '0%': { opacity: 0, transform: 'translateY(-2px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideLeftAndFade = keyframes({
    '0%': { opacity: 0, transform: 'translateX(2px)' },
    '100%': { opacity: 1, transform: 'translateX(0)' },
});

export const dropdownStyles = {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    textAlign: 'left',
    backgroundColor: colors.shadesWhite,
    fontSize: 13,
    lineHeight: 1,
    borderRadius: borderRadius.small,
    gap: 6,
    position: 'relative',
    userSelect: 'none',
    transition: '0.2s',
    '&[data-disabled]': {
        pointerEvents: 'none',
    },
    '&:hover': {
        backgroundColor: colors.grey50,
    },
    '&[data-state="open"]': {
        backgroundColor: colors.grey100,
    },
} as Interpolation<Theme>;

const Dropdown = ({
    title,
    children,
    name,
    disabled,
    className,
    align = 'center',
    dropdownProps = {
        root: {},
        trigger: {},
        portal: {},
        content: {},
    },
}: DropdownProps): JSX.Element => (
    <DropdownMenu.Root {...dropdownProps.root}>
        <DropdownMenu.Trigger
            css={dropdownStyles}
            className={className}
            aria-label={name}
            disabled={disabled}
            aria-disabled={disabled}
            {...dropdownProps.trigger}
        >
            {title}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal {...dropdownProps.portal}>
            <DropdownMenu.Content
                align={align}
                sideOffset={5}
                css={{
                    zIndex: 100,
                    // minWidth: 220,
                    backgroundColor: 'white',
                    borderRadius: 6,
                    // padding: 5,
                    boxShadow:
                        '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
                    animationDuration: '400ms',
                    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                    willChange: 'transform, opacity',
                    '&[data-state="open"]': {
                        '&[data-side="top"]': { animationName: slideDownAndFade },
                        '&[data-side="right"]': { animationName: slideLeftAndFade },
                        '&[data-side="bottom"]': { animationName: slideUpAndFade },
                        '&[data-side="left"]': { animationName: slideRightAndFade },
                    },
                }}
                {...dropdownProps.content}
            >
                {children}
            </DropdownMenu.Content>
        </DropdownMenu.Portal>
    </DropdownMenu.Root>
);

export const DropdownSeparator = (props: DropdownMenu.MenuSeparatorProps): JSX.Element => (
    <DropdownMenu.DropdownMenuSeparator
        css={{
            height: 1,
            backgroundColor: '#eee',
            margin: 5,
        }}
        {...props}
    />
);

export const DropdownItem = (props: DropdownMenu.DropdownMenuItemProps): JSX.Element => (
    <DropdownMenu.Item
        css={{
            fontSize: 13,
            lineHeight: 1,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            height: 25,
            padding: '0 5px',
            position: 'relative',
            paddingLeft: 25,
            paddingRight: 35,
            userSelect: 'none',
            '&[data-disabled]': {
                pointerEvents: 'none',
            },
            '&[data-highlighted]': {
                backgroundColor: '#eee',
            },
        }}
        {...props}
    />
);

export const CheckboxItem = (props: DropdownMenu.DropdownMenuCheckboxItemProps): JSX.Element => (
    <DropdownMenu.CheckboxItem
        css={{
            fontSize: 13,
            lineHeight: 1,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            height: 25,
            padding: '0 5px',
            position: 'relative',
            paddingLeft: 25,
            paddingRight: 35,
            userSelect: 'none',
            '&[data-disabled]': {
                pointerEvents: 'none',
            },
            '&[data-highlighted]': {
                backgroundColor: '#eee',
            },
            '.DropdownMenuItemIndicator': {
                backgroundColor: colors.primaryOrange,
                borderRadius: borderRadius.small,
                color: colors.shadesWhite,
            }
        }}
        {...props}
    >
            {props.children}
         <DropdownMenu.ItemIndicator className="DropdownMenuItemIndicator">
         <CheckIcon />
            </DropdownMenu.ItemIndicator>
    </DropdownMenu.CheckboxItem>
);


export default Dropdown;
